import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Pencil, Trash2 } from 'lucide-react';
import ExpandableSearchFilter from './ExpandableSearchFilter';
import { showSuccessToast, showErrorToast } from '../utils/notifications';
import useSubmitLock from '../hooks/useSubmitLock';
import { getSessionRole } from '../services/authSession';

// Converte entrada decimal pt-BR ("1,5") em número (1.5).
export function parseDecimalInput(value) {
  return Number(String(value).replace(',', '.'));
}

// Formata número para exibição pt-BR ("0.85" -> "0,85").
function formatDecimalPtBr(value) {
  return String(value).replace('.', ',');
}

// Mensagem de faixa permitida de um campo decimal com `min`/`max`.
function buildRangeMessage(campo) {
  const { label, min, max } = campo;
  if (min != null && max != null) {
    return `${label} deve ficar entre ${formatDecimalPtBr(min)} e ${formatDecimalPtBr(max)}.`;
  }
  if (min != null) return `${label} não pode ser menor que ${formatDecimalPtBr(min)}.`;
  return `${label} não pode ser maior que ${formatDecimalPtBr(max)}.`;
}

// Multisseleção guarda sempre uma lista de strings, mesmo quando a API devolve um valor único.
function toValueList(value) {
  if (Array.isArray(value)) return value.map(String);
  return value === '' || value == null ? [] : [String(value)];
}

// Rótulo de uma opção de select/multiselect; cai no próprio valor quando a opção não está na lista.
function labelDaOpcao(campo, value) {
  const option = (campo.options || []).find((opt) => String(opt.value) === String(value));
  return option ? option.label : String(value);
}

// Valor vazio de um campo, conforme o tipo.
function valorVazio(campo) {
  if (campo.type === 'checkbox') return false;
  if (campo.type === 'multiselect') return [];
  return '';
}

// Formulário zerado a partir da configuração de campos.
function buildInitialForm(campos) {
  return campos.reduce((acc, campo) => ({ ...acc, [campo.name]: valorVazio(campo) }), {});
}

// Formulário preenchido com os valores de um item já normalizado.
function buildFormFromItem(campos, item) {
  return campos.reduce((acc, campo) => {
    const value = item[campo.name];
    if (campo.type === 'checkbox') return { ...acc, [campo.name]: Boolean(value) };
    if (campo.type === 'multiselect') return { ...acc, [campo.name]: toValueList(value) };
    return { ...acc, [campo.name]: value == null ? '' : String(value) };
  }, {});
}

// Texto do campo usado na tabela e na busca (sempre string, mesmo para colunas com render próprio).
function formatFieldValue(campo, item) {
  const value = item[campo.name];
  if (campo.type === 'multiselect') {
    const selecionados = toValueList(value);
    return selecionados.length ? selecionados.map((v) => labelDaOpcao(campo, v)).join(', ') : '-';
  }
  if (value === '' || value == null) return '-';
  if (campo.type === 'select') return labelDaOpcao(campo, value);
  return String(value);
}

// Opção marcada usa o mesmo destaque do item ativo da sidebar.
const OPCAO_MARCADA_CLASSE = 'bg-[#0e7490]/10 text-[#0e7490]';

// Texto do botão do multiselect: nome da opção quando só uma está marcada, contagem quando são várias.
function resumoMultiSelect(campo, valores, todasMarcadas) {
  if (todasMarcadas) return campo.labelTodas || 'Todas';
  if (valores.length === 0) return campo.placeholder || 'Selecione';
  if (valores.length === 1) return labelDaOpcao(campo, valores[0]);
  return `${valores.length} selecionadas`;
}

// Multisseleção com a cara de um select: o botão abre um painel com uma caixa de marcação por opção,
// mais um "todas" no topo que marca/desmarca a lista inteira.
function MultiSelectField({ campo, valores, onToggle, onSetAll }) {
  const [aberto, setAberto] = useState(false);
  const containerRef = useRef(null);
  const options = campo.options || [];
  const todasMarcadas = options.length > 0 && valores.length === options.length;

  // Fecha ao clicar fora do campo.
  useEffect(() => {
    if (!aberto) return undefined;
    const handleClickFora = (event) => {
      if (!containerRef.current?.contains(event.target)) setAberto(false);
    };
    document.addEventListener('mousedown', handleClickFora);
    return () => document.removeEventListener('mousedown', handleClickFora);
  }, [aberto]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setAberto((prev) => !prev)}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-left text-sm text-slate-700"
      >
        <span className={valores.length ? '' : 'text-slate-400'}>
          {resumoMultiSelect(campo, valores, todasMarcadas)}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition ${aberto ? 'rotate-180' : ''}`} />
      </button>

      {aberto ? (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {options.length === 0 ? (
            <p className="px-3 py-2 text-sm text-slate-400">{campo.vazio || 'Nenhuma opção disponível.'}</p>
          ) : (
            <>
              <label className={`flex cursor-pointer items-center justify-between gap-2 border-b border-slate-100 px-3 py-2 text-sm font-medium ${todasMarcadas ? OPCAO_MARCADA_CLASSE : 'text-slate-700 hover:bg-slate-50'}`}>
                <span>{campo.labelTodas || 'Todas'}</span>
                <input
                  type="checkbox"
                  checked={todasMarcadas}
                  onChange={() => onSetAll(todasMarcadas ? [] : options.map((opt) => String(opt.value)))}
                  className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600"
                />
              </label>
              {options.map((option) => {
                const marcada = valores.includes(String(option.value));
                return (
                  <label
                    key={option.value}
                    className={`flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm ${marcada ? OPCAO_MARCADA_CLASSE : 'text-slate-700 hover:bg-slate-50'}`}
                  >
                    <span>{option.label}</span>
                    <input
                      type="checkbox"
                      checked={marcada}
                      onChange={() => onToggle(String(option.value))}
                      className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600"
                    />
                  </label>
                );
              })}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

// Conteúdo da célula: campos com `render` desenham o próprio nó (badges, por exemplo).
function renderCellValue(campo, item) {
  return campo.render ? campo.render(item) : formatFieldValue(campo, item);
}

// Seção de cadastro simples (nome + poucos campos) reutilizada pelas abas de Cadastros técnicos.
// O formulário fica fixo na tela (sem modal), servindo tanto para criar quanto para editar.
// Campos com `somenteLeitura` só aparecem na tabela; com `apenasFormulario`, só no formulário.
function CadastroSimplesSection({
  titulo,
  descricao,
  labelNovo,
  labelEdicao,
  labelSalvar,
  avisoFormulario,
  mensagens,
  campos,
  listar,
  criar,
  atualizar,
  excluir,
  normalizar,
  montarPayload,
  podeEditar,
  podeExcluir,
  labelAcaoEditar,
  carregando: carregandoExterno = false,
}) {
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState(() => buildInitialForm(campos));
  const [itens, setItens] = useState([]);
  const formRef = useRef(null);

  const role = getSessionRole();
  const canManage = role === 'admin';
  const canCreate = role === 'admin' || role === 'consultor' || !role;

  const carregarItens = useCallback(async () => {
    const data = await listar();
    return Array.isArray(data) ? data.map(normalizar) : [];
  }, [listar, normalizar]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const lista = await carregarItens();
        if (isMounted) setItens(lista);
      } catch (error) {
        if (isMounted) setItens([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [carregarItens]);

  const colunas = useMemo(() => campos.filter((campo) => !campo.apenasFormulario), [campos]);
  const camposFormulario = useMemo(() => campos.filter((campo) => !campo.somenteLeitura), [campos]);

  const filteredItens = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return itens;
    return itens.filter((item) => colunas.some((campo) => (
      formatFieldValue(campo, item).toLowerCase().includes(normalized)
    )));
  }, [itens, query, colunas]);

  const handleFieldChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.checked }));
  };

  const handleMultiSelectSetAll = (field) => (values) => {
    setFormData((prev) => ({ ...prev, [field]: values }));
  };

  const handleMultiSelectToggle = (field) => (value) => {
    setFormData((prev) => {
      const atual = toValueList(prev[field]);
      const marcado = atual.includes(value);
      return {
        ...prev,
        [field]: marcado ? atual.filter((item) => item !== value) : [...atual, value],
      };
    });
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData(buildInitialForm(camposFormulario));
    setFormErrors({});
  };

  // Carrega o item no formulário fixo e traz o formulário para a visão do usuário.
  const startEdit = (item) => {
    setEditingId(item.id);
    setFormData(buildFormFromItem(camposFormulario, item));
    setFormErrors({});
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const validateForm = () => {
    const errors = {};
    camposFormulario.forEach((campo) => {
      if (campo.type === 'checkbox') return;
      const obrigatorio = campo.mensagemObrigatorio
        || `${campo.label} obrigatório${campo.generoFeminino ? 'a' : ''}.`;
      if (campo.type === 'multiselect') {
        if (campo.required && toValueList(formData[campo.name]).length === 0) {
          errors[campo.name] = obrigatorio;
        }
        return;
      }
      const value = String(formData[campo.name] ?? '').trim();
      if (campo.required && !value) {
        errors[campo.name] = obrigatorio;
        return;
      }
      if (campo.type === 'decimal' && value) {
        const numero = parseDecimalInput(value);
        if (!Number.isFinite(numero)) {
          errors[campo.name] = `${campo.label} inválido${campo.generoFeminino ? 'a' : ''}.`;
        } else if ((campo.min != null && numero < campo.min) || (campo.max != null && numero > campo.max)) {
          errors[campo.name] = buildRangeMessage(campo);
        }
      }
    });
    return errors;
  };

  const [submitting, handleSave] = useSubmitLock(async (event) => {
    event.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      if (editingId) {
        await atualizar(editingId, montarPayload(formData));
        showSuccessToast(mensagens.atualizado);
      } else {
        await criar(montarPayload(formData));
        showSuccessToast(mensagens.criado);
      }
      // Recarrega a lista para reaproveitar os ids e campos calculados devolvidos pelo backend.
      setItens(await carregarItens());
      resetForm();
    } catch (error) {
      setFormErrors({ geral: error?.message || mensagens.erroSalvar });
    }
  });

  const handleDelete = async (id) => {
    if (!window.confirm(mensagens.confirmarExclusao)) return;

    try {
      await excluir(id);
      setItens((prev) => prev.filter((item) => item.id !== id));
      if (editingId === id) resetForm();
      showSuccessToast(mensagens.excluido);
    } catch (error) {
      showErrorToast(error?.message || mensagens.erroExcluir);
    }
  };

  const isLoading = loading || carregandoExterno;
  const colSpan = colunas.length + 1;
  const showForm = canCreate || canManage;
  const itemPodeEditar = (item) => (podeEditar ? podeEditar(item) : canManage);
  const itemPodeExcluir = (item) => (podeExcluir ? podeExcluir(item) : canManage);

  return (
    <div className="space-y-4">
      {/* Título, descrição e formulário dividem o mesmo card. */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900">{titulo}</h3>
        {/* whitespace-pre-line: descrições com quebra de linha explícita (\n) */}
        <p className="mt-1 max-w-2xl whitespace-pre-line text-sm text-slate-500">{descricao}</p>

        {showForm ? (
          <form
            ref={formRef}
            onSubmit={handleSave}
            className="mt-5"
          >
            <div className="flex items-center gap-2">
              {editingId ? <Pencil className="h-4 w-4 text-slate-400" /> : null}
              <p className="text-sm font-semibold text-slate-900">
                {editingId ? labelEdicao : `${labelNovo}:`}
              </p>
            </div>

            {avisoFormulario ? (
              <div className="mt-4 max-w-2xl rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                {avisoFormulario}
              </div>
            ) : null}

            {formErrors.geral ? (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {formErrors.geral}
              </div>
            ) : null}

            <div className="mt-4 space-y-4">
              {camposFormulario.map((campo) => (
                <div key={campo.name} className="space-y-2">
                  {campo.type === 'checkbox' ? (
                    <label className="flex max-w-2xl items-start gap-2 text-sm font-medium text-slate-700">
                      <input
                        type="checkbox"
                        checked={Boolean(formData[campo.name])}
                        onChange={handleCheckboxChange(campo.name)}
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600"
                      />
                      <span>{campo.label}</span>
                    </label>
                  ) : (
                    /* Label e campo ficam estreitos; a dica usa a largura do card para não quebrar em muitas linhas. */
                    <div className={`space-y-2 ${campo.type === 'textarea' ? 'sm:max-w-2xl' : 'sm:max-w-sm'}`}>
                      <label className="block text-sm font-medium text-slate-700">
                        {campo.label} {campo.required ? <span className="text-rose-500">*</span> : null}
                      </label>
                      {campo.type === 'multiselect' ? (
                        <MultiSelectField
                          campo={campo}
                          valores={toValueList(formData[campo.name])}
                          onToggle={handleMultiSelectToggle(campo.name)}
                          onSetAll={handleMultiSelectSetAll(campo.name)}
                        />
                      ) : campo.type === 'select' ? (
                        <select
                          value={formData[campo.name]}
                          onChange={handleFieldChange(campo.name)}
                          className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                        >
                          <option value="">{campo.placeholder || 'Selecione'}</option>
                          {(campo.options || []).map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      ) : campo.type === 'textarea' ? (
                        <textarea
                          value={formData[campo.name]}
                          onChange={handleFieldChange(campo.name)}
                          placeholder={campo.placeholder}
                          rows={3}
                          className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                        />
                      ) : (
                        <input
                          type="text"
                          value={formData[campo.name]}
                          onChange={handleFieldChange(campo.name)}
                          placeholder={campo.placeholder}
                          className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                        />
                      )}
                    </div>
                  )}
                  {campo.hint ? <p className="max-w-2xl text-sm text-slate-500">{campo.hint}</p> : null}
                  {formErrors[campo.name] ? (
                    <p className="text-sm text-rose-600">{formErrors[campo.name]}</p>
                  ) : null}
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {submitting ? 'Salvando...' : editingId ? 'Salvar alterações' : (labelSalvar || 'Adicionar')}
              </button>
              {editingId ? (
                <button
                  type="button"
                  onClick={resetForm}
                  className="w-full rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600 sm:w-auto"
                >
                  Cancelar edição
                </button>
              ) : null}
            </div>
          </form>
        ) : null}
      </section>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3 sm:hidden">
          <ExpandableSearchFilter
            query={query}
            onChange={setQuery}
            placeholder="Buscar por nome"
            resultCount={filteredItens.length}
          />
        </div>

        <div className="hidden overflow-x-auto sm:block">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-[0.3em] text-slate-400">
              <tr>
                {colunas.map((campo) => (
                  <th key={campo.name} className="px-4 py-3">{campo.colunaLabel || campo.label}</th>
                ))}
                <th className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <span>Ações</span>
                    <ExpandableSearchFilter
                      variant="inline"
                      query={query}
                      onChange={setQuery}
                      placeholder="Buscar..."
                      resultCount={filteredItens.length}
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, index) => (
                  <tr key={`sk-${index}`} className="border-b border-slate-100">
                    {Array.from({ length: colSpan }).map((__, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-4">
                        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredItens.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 py-6 text-center text-sm text-slate-500">
                    {mensagens.vazio}
                  </td>
                </tr>
              ) : (
                filteredItens.map((item) => (
                  <tr
                    key={item.id}
                    className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/80 ${
                      editingId === item.id ? 'bg-[#0e7490]/5' : ''
                    }`}
                  >
                    {colunas.map((campo, index) => (
                      <td
                        key={campo.name}
                        className={`px-4 py-4 ${index === 0 ? 'font-medium text-slate-900' : 'text-slate-600'}`}
                      >
                        {renderCellValue(campo, item)}
                      </td>
                    ))}
                    <td className="px-4 py-4">
                      {itemPodeEditar(item) || itemPodeExcluir(item) ? (
                        <div className="flex items-center gap-2">
                          {itemPodeEditar(item) ? (
                            <button
                              type="button"
                              onClick={() => startEdit(item)}
                              className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                              {labelAcaoEditar ? labelAcaoEditar(item) : 'Editar'}
                            </button>
                          ) : null}
                          {itemPodeExcluir(item) ? (
                            <button
                              type="button"
                              onClick={() => handleDelete(item.id)}
                              className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Excluir
                            </button>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">Somente leitura</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 sm:hidden">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div key={`mobile-sk-${index}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
                <div className="mt-3 h-3 w-2/3 animate-pulse rounded bg-slate-100" />
              </div>
            ))
          ) : filteredItens.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              {mensagens.vazio}
            </div>
          ) : (
            filteredItens.map((item) => (
              <div
                key={item.id}
                className={`rounded-xl border p-4 shadow-sm ${
                  editingId === item.id ? 'border-[#0e7490]/40 bg-[#0e7490]/5' : 'border-slate-200 bg-white'
                }`}
              >
                <p className="text-base font-semibold text-slate-900">{renderCellValue(colunas[0], item)}</p>
                {colunas.slice(1).map((campo) => (
                  <p key={campo.name} className="mt-1 flex flex-wrap items-center gap-1 text-xs text-slate-600">
                    <span>{campo.colunaLabel || campo.label}:</span>
                    {renderCellValue(campo, item)}
                  </p>
                ))}
                {itemPodeEditar(item) || itemPodeExcluir(item) ? (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {itemPodeEditar(item) ? (
                      <button
                        type="button"
                        onClick={() => startEdit(item)}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        {labelAcaoEditar ? labelAcaoEditar(item) : 'Editar'}
                      </button>
                    ) : null}
                    {itemPodeExcluir(item) ? (
                      <button
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Excluir
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

export default CadastroSimplesSection;
